import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Server, Database, Shield, Globe, Cpu, HardDrive,
  Radio, Activity, Layers, MonitorCheck,
  ZoomIn, ZoomOut, Maximize, RefreshCw, Loader, AlertCircle
} from 'lucide-react';

// Constantes pour les dimensions
const GRID_SIZE = 20;
const COMPONENT_WIDTH = GRID_SIZE * 12;
const COMPONENT_HEIGHT = GRID_SIZE * 8;
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

// Import dynamique de tous les fichiers JSON du répertoire infrastructure
const infraContext = require.context('../data/infrastructure', false, /\.json$/);

// Component type definitions with icons and colors
const COMPONENT_TYPES = {
  PROXY: {
    icon: Shield,
    color: '#3B82F6',
    label: 'Proxy Server'
  },
  SERVER: {
    icon: Server,
    color: '#10B981',
    label: 'Application Server'
  },
  DATABASE: {
    icon: Database,
    color: '#6366F1',
    label: 'Database Server'
  },
  CACHE: {
    icon: HardDrive,
    color: '#F59E0B',
    label: 'Cache Server'
  },
  QUEUE: {
    icon: Radio,
    color: '#EC4899',
    label: 'Message Queue'
  },
  LOAD_BALANCER: {
    icon: Activity,
    color: '#8B5CF6',
    label: 'Load Balancer'
  },
  FIREWALL: {
    icon: Shield,
    color: '#EF4444',
    label: 'Firewall'
  },
  STORAGE: {
    icon: HardDrive,
    color: '#06B6D4',
    label: 'Storage Server'
  }
};

// Connection type definitions with styles and animations
const CONNECTION_TYPES = {
  HTTP: {
    name: 'HTTP/HTTPS',
    color: '#3B82F6',
    pattern: [1, 0],
    icon: Globe,
    label: 'Trafic Web',
    animationDuration: '10s'
  },
  'HTTP/HTTPS': {
    name: 'HTTP/HTTPS',
    color: '#3B82F6',
    pattern: [1, 0],
    icon: Globe,
    label: 'Trafic Web',
    animationDuration: '10s'
  },
  TCP: {
    name: 'TCP/IP',
    color: '#10B981',
    pattern: [5, 5],
    icon: Activity,
    label: 'Connexion Base de Données',
    animationDuration: '8s'
  },
  WebSocket: {
    name: 'WebSocket',
    color: '#8B5CF6',
    pattern: [3, 3],
    icon: Radio,
    label: 'Communication Temps Réel',
    animationDuration: '6s'
  },
  UDP: {
    name: 'UDP',
    color: '#F59E0B',
    pattern: [2, 2],
    icon: Layers,
    label: 'Transfert Rapide',
    animationDuration: '6s'
  },
  INTERNAL: {
    name: 'Internal',
    color: '#EC4899',
    pattern: [4, 2],
    icon: Server,
    label: 'Communication Interne',
    animationDuration: '12s'
  }
};

// Helper pour détecter le type d'infrastructure depuis les métadonnées ou le nom de fichier
const detectInfrastructureType = (metadata, filename) => {
  const title = metadata?.title?.toLowerCase() || '';
  const filenameLC = filename.toLowerCase();
  
  if (filenameLC.includes('production') || title.includes('production')) {
    return {
      icon: Server,
      name: 'Infrastructure Production',
      description: 'Infrastructure de production complète'
    };
  }
  if (filenameLC.includes('staging') || title.includes('staging')) {
    return {
      icon: Layers,
      name: 'Infrastructure Staging',
      description: 'Environnement de pré-production'
    };
  }
  if (filenameLC.includes('development') || title.includes('développement')) {
    return {
      icon: Cpu,
      name: 'Infrastructure Développement',
      description: 'Environnement de développement'
    };
  }
  if (filenameLC.includes('network') || title.includes('réseau')) {
    return {
      icon: Globe,
      name: 'Infrastructure Réseau',
      description: 'Topologie réseau et sécurité'
    };
  }
  
  // Type par défaut
  return {
    icon: Server,
    name: metadata?.title || 'Infrastructure',
    description: metadata?.description || 'Documentation infrastructure'
  };
};

// Validation de structure d'un fichier d'infrastructure
const validateInfrastructureStructure = (data, filename) => {
  const errors = [];
  
  // Vérifier les métadonnées
  if (!data.metadata) {
    errors.push('Métadonnées manquantes');
  }
  
  // Vérifier les composants
  if (!data.components || typeof data.components !== 'object') {
    errors.push('Composants manquants ou invalides');
  }
  
  // Vérifier que chaque composant a les champs requis
  if (data.components) {
    Object.entries(data.components).forEach(([id, component]) => {
      if (!component.type) errors.push(`Composant ${id}: type manquant`);
      if (!component.name) errors.push(`Composant ${id}: nom manquant`);
      if (component.x === undefined) errors.push(`Composant ${id}: position x manquante`);
      if (component.y === undefined) errors.push(`Composant ${id}: position y manquante`);
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

// Extraire les métadonnées d'un fichier d'infrastructure
const extractInfrastructureMetadata = (data, filename) => {
  const typeInfo = detectInfrastructureType(data.metadata, filename);
  
  // Compter les éléments
  const components = Object.values(data.components || {});
  const zones = [...new Set(components.map(c => c.zone).filter(Boolean))];
  const types = [...new Set(components.map(c => c.type))];
  const statusCounts = components.reduce((acc, comp) => {
    acc[comp.status] = (acc[comp.status] || 0) + 1;
    return acc;
  }, {});
  
  return {
    filename,
    name: typeInfo.name,
    icon: typeInfo.icon,
    description: typeInfo.description,
    version: data.metadata?.version || '1.0.0',
    lastUpdate: data.metadata?.lastUpdate || new Date().toISOString(),
    componentsCount: components.length,
    zonesCount: zones.length,
    typesCount: types.length,
    activeCount: statusCounts.active || 0
  };
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  disabled = false,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Component for individual infrastructure elements
const InfraComponent = ({ component, allComponents }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    type,
    name,
    hostname,
    status,
    ip,
    specs,
    software = [],
    x,
    y,
    description,
    zone
  } = component;
  
  const componentType = COMPONENT_TYPES[type];
  const Icon = componentType?.icon || Server;

  // Calculer la couleur de zone
  const getZoneColor = (zone) => {
    const zoneColors = {
      'dmz': '#FEF3C7',
      'private_cloud': '#DBEAFE',
      'public_cloud': '#D1FAE5',
      'internal': '#E0E7FF',
      'external': '#FEE2E2',
      'external_zone': '#E5F3FF',
      'frontend_zone': '#FFF7E6',
      'backend_zone': '#FFF7E6',
      'amp_zone': '#E5F3FF'
    };
    return zoneColors[zone] || '#F3F4F6';
  };

  return (
    <div
      className="absolute transition-transform duration-200 ease-in-out"
      style={{
        transform: `translate(${x}px, ${y}px)`,
        width: COMPONENT_WIDTH,
        height: isExpanded ? 'auto' : COMPONENT_HEIGHT,
        zIndex: isExpanded ? 50 : 1
      }}
    >
      <div 
        className={`relative w-full h-full p-4 rounded-xl border-2 
          ${status === 'active' ? 'border-green-300' : 
            status === 'warning' ? 'border-yellow-300' : 
            status === 'error' ? 'border-red-300' : 'border-gray-300'}
          bg-white shadow-lg hover:shadow-xl transition-shadow duration-200
          ${isExpanded ? 'ring-2 ring-blue-400' : ''}
        `}
        style={{ backgroundColor: getZoneColor(zone) }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3 mb-3">
          <Icon 
            className="w-6 h-6"
            style={{ color: componentType?.color }}
          />
          <div>
            <h3 className="font-semibold text-gray-800">{name}</h3>
            <p className="text-xs text-gray-500">{hostname}</p>
          </div>
          <span className={`
            ml-auto px-2 py-1 text-xs rounded-full font-medium
            ${status === 'active' ? 'bg-green-100 text-green-800' : 
              status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
              status === 'error' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}
          `}>
            {status}
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">{ip}</span>
          </div>
          
          {zone && (
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Zone: {zone}</span>
            </div>
          )}
          
          {/* SPÉCIFICATIONS DÉPLACÉES DANS LA SECTION DÉVELOPPÉE */}
          {isExpanded && specs && (
            <div className="border-t pt-3">
              <h4 className="font-medium text-gray-700 mb-2">Spécifications</h4>
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(specs).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <MonitorCheck className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {key}: <span className="font-bold">{value}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isExpanded && (
            <div className="mt-4 space-y-4">
              {description && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 mb-1">Description</h4>
                  <p className="text-gray-600 text-sm">{description}</p>
                </div>
              )}

              {software.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="font-medium text-gray-700 mb-2">Logiciels installés</h4>
                  <div className="space-y-2">
                    {software.map((sw, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700 font-medium">{sw.name}</span>
                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">{sw.version}</span>
                        </div>
                        <span className={`
                          px-2 py-0.5 text-xs rounded-full font-medium
                          ${sw.status === 'running' ? 'bg-green-100 text-green-700' : 
                            sw.status === 'stopped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}
                        `}>
                          {sw.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ZoomControls = ({ zoom, setZoom }) => {
  const handleZoomIn = () => setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  const handleReset = () => setZoom(1);

  return (
    <div className="fixed bottom-4 right-4 flex gap-2 bg-white p-2 rounded-lg shadow-lg z-50">
      <button
        onClick={handleZoomIn}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Zoom In"
      >
        <ZoomIn size={20} />
      </button>
      <button
        onClick={handleZoomOut}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Zoom Out"
      >
        <ZoomOut size={20} />
      </button>
      <button
        onClick={handleReset}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Reset Zoom"
      >
        <Maximize size={20} />
      </button>
      <span className="flex items-center px-2 text-sm text-gray-600">
        {Math.round(zoom * 100)}%
      </span>
    </div>
  );
};

const Grid = ({ zoom }) => {
  const gridSize = GRID_SIZE * zoom;
  return (
    <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
      <defs>
        <pattern 
          id="grid" 
          width={gridSize} 
          height={gridSize} 
          patternUnits="userSpaceOnUse"
        >
          <path 
            d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} 
            fill="none" 
            stroke="rgba(0,0,0,0.1)" 
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
};

// Component for connection lines between infrastructure components
const ConnectionLine = ({ source, target, points, type, allComponents, connectionData }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Utiliser le protocol depuis connectionData si disponible, sinon utiliser type
  const protocolType = connectionData?.protocol || type || 'TCP';
  const connectionStyle = CONNECTION_TYPES[protocolType] || CONNECTION_TYPES.TCP;
  
  const startX = source.x + COMPONENT_WIDTH / 2;
  const startY = source.y + COMPONENT_HEIGHT / 2;
  const targetComponent = allComponents[target];
  
  if (!targetComponent) return null;
  
  const endX = targetComponent.x + COMPONENT_WIDTH / 2;
  const endY = targetComponent.y + COMPONENT_HEIGHT / 2;
  
  const pathId = `path-${source.id}-${target}`;
  const ConnectionIcon = connectionStyle.icon;

  const createPath = () => {
    let path = `M ${startX} ${startY}`;
    if (points && points.length > 0) {
      points.forEach(point => path += ` L ${point[0]} ${point[1]}`);
    }
    path += ` L ${endX} ${endY}`;
    return path;
  };

  const pathD = createPath();
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="cursor-pointer"
      style={{ pointerEvents: 'all' }}
    >
      <path
        id={pathId}
        d={pathD}
        stroke={connectionStyle.color}
        strokeWidth={isHovered ? 4 : 3}
        fill="none"
        strokeDasharray={connectionStyle.pattern}
        opacity={isHovered ? "0.9" : "0.6"}
      />
      
      {/* Animation de flux */}
      {[0, 1, 2].map((i) => (
        <circle 
          key={i}
          r="4" 
          fill={connectionStyle.color}
          opacity="0.8"
        >
          <animateMotion
            dur={connectionStyle.animationDuration}
            repeatCount="indefinite"
            path={pathD}
            begin={`-${(parseFloat(connectionStyle.animationDuration) * i) / 3}s`}
          />
        </circle>
      ))}

      <circle cx={startX} cy={startY} r="5" fill={connectionStyle.color} />
      <circle cx={endX} cy={endY} r="5" fill={connectionStyle.color} />

      <g transform={`translate(${midX - 12}, ${midY - 12})`}>
        <rect
          width="24"
          height="24"
          fill="white"
          stroke={connectionStyle.color}
          strokeWidth="2"
          rx="6"
          className={`${isHovered ? 'filter drop-shadow-md' : ''}`}
        />
        <foreignObject width="24" height="24">
          <div className="w-full h-full flex items-center justify-center">
            <ConnectionIcon 
              className="w-4 h-4" 
              style={{ color: connectionStyle.color }} 
            />
          </div>
        </foreignObject>
      </g>

      {isHovered && (
        <g transform={`translate(${midX}, ${midY - 40})`}>
          <rect
            x="-100"
            y="-40"
            width="200"
            height="80"
            fill="white"
            stroke={connectionStyle.color}
            strokeWidth="1"
            rx="4"
            className="filter drop-shadow-lg"
          />
          <text
            textAnchor="middle"
            y="-20"
            className="text-sm font-medium fill-gray-700"
          >
            {connectionStyle.name}
          </text>
          <text
            textAnchor="middle"
            y="-5"
            className="text-xs fill-gray-500"
          >
            {connectionStyle.label}
          </text>
          <text
            textAnchor="middle"
            y="15"
            className="text-xs fill-gray-500"
          >
            {`${source.name} → ${targetComponent.name}`}
          </text>
          {connectionData?.description && (
            <text
              textAnchor="middle"
              y="30"
              className="text-xs fill-gray-400"
            >
              {connectionData.description}
            </text>
          )}
        </g>
      )}
    </g>
  );
};

const InfrastructureGrid = ({ infrastructureData }) => {
  const [zoom, setZoom] = useState(1);
  
  if (!infrastructureData || !infrastructureData.components) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune donnée d'infrastructure disponible</p>
        </div>
      </div>
    );
  }

  const { components } = infrastructureData;

  // Style for the zoomed content
  const contentStyle = {
    transform: `scale(${zoom})`,
    transformOrigin: '0 0',
    width: `${100 / zoom}%`,
    height: `${100 / zoom}%`
  };

  return (
    <div className="relative w-full h-screen bg-gray-50 overflow-auto">
      {/* Grid Background */}
      <Grid zoom={zoom} />

      {/* Zoom Controls */}
      <ZoomControls zoom={zoom} setZoom={setZoom} />

      {/* Zoomed Content Container */}
      <div style={contentStyle}>
        {/* SVG Connections Layer */}
        <svg 
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: 'none' }}
        >
          <g style={{ pointerEvents: 'all' }}>
            {Object.values(components).map(component => 
              component.connections?.map((conn, idx) => (
                <ConnectionLine
                  key={`${component.id}-${conn.target}-${idx}`}
                  source={component}
                  target={conn.target}
                  points={conn.points}
                  type={conn.type}
                  connectionData={conn}
                  allComponents={components}
                />
              ))
            )}
          </g>
        </svg>

        {/* Infrastructure Components */}
        {Object.values(components).map(component => (
          <InfraComponent
            key={component.id}
            component={component}
            allComponents={components}
          />
        ))}
      </div>
    </div>
  );
};

// Statistics component
const InfrastructureStats = ({ infrastructureData }) => {
  if (!infrastructureData || !infrastructureData.components) return null;

  const components = Object.values(infrastructureData.components);
  const zones = [...new Set(components.map(c => c.zone).filter(Boolean))];
  const types = [...new Set(components.map(c => c.type))];
  const statusCounts = components.reduce((acc, comp) => {
    acc[comp.status] = (acc[comp.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{components.length}</div>
            <div className="text-sm text-gray-600">Composants</div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{zones.length}</div>
            <div className="text-sm text-gray-600">Zones</div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{types.length}</div>
            <div className="text-sm text-gray-600">Types</div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{statusCounts.active || 0}</div>
            <div className="text-sm text-gray-600">Actifs</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Composant principal
const DocumentInfrastructure = () => {
  const [infrastructures, setInfrastructures] = useState([]);
  const [selectedInfrastructure, setSelectedInfrastructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les infrastructures au montage
  useEffect(() => {
    loadInfrastructures();
  }, []);

  const loadInfrastructures = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Démarrage du chargement des infrastructures...');
      
      const keys = infraContext.keys();
      const loadedInfrastructures = [];
      const failedFiles = [];
      
      // Parcourir tous les fichiers trouvés
      keys.forEach(key => {
        const filename = key.replace('./', '');
        
        try {
          // Charger le fichier
          const data = infraContext(key);
          
          // Valider la structure
          const validation = validateInfrastructureStructure(data, filename);
          
          if (validation.isValid) {
            // Extraire les métadonnées
            const metadata = extractInfrastructureMetadata(data, filename);
            
            loadedInfrastructures.push({
              ...metadata,
              data
            });
          } else {
            failedFiles.push({
              filename,
              errors: validation.errors
            });
          }
        } catch (err) {
          failedFiles.push({
            filename,
            error: err.message
          });
          console.error(`Erreur lors du chargement de ${filename}:`, err);
        }
      });
      
      console.log(`📁 ${loadedInfrastructures.length} infrastructures chargées`);
      
      // Trier par nom
      loadedInfrastructures.sort((a, b) => a.name.localeCompare(b.name));
      
      setInfrastructures(loadedInfrastructures);
      if (loadedInfrastructures.length > 0) {
        setSelectedInfrastructure(loadedInfrastructures[0]);
        console.log(`✅ Première infrastructure sélectionnée: ${loadedInfrastructures[0].name}`);
      }
      
      // Log des fichiers avec erreurs (pour debug)
      if (failedFiles.length > 0) {
        console.log('⚠️ Fichiers avec erreurs:', failedFiles);
      }
    } catch (err) {
      setError('Erreur lors du chargement des infrastructures');
      console.error('Error loading infrastructures:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Chargement des infrastructures...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent>
              <div className="flex items-center space-x-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
              <Button
                onClick={loadInfrastructures}
                className="mt-4"
                variant="outline"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto">
        {/* Header avec sélection d'infrastructure */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Documentation Infrastructure
              </h1>
              <p className="mt-1 text-gray-600">
                {infrastructures.length} environnements disponibles
              </p>
            </div>
            <Button
              variant="outline"
              onClick={loadInfrastructures}
              className="flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Actualiser</span>
            </Button>
          </div>

          {/* Sélection d'infrastructure */}
          <div className="mt-4">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {infrastructures.map((infra) => {
                const Icon = infra.icon;
                return (
                  <button
                    key={infra.filename}
                    onClick={() => setSelectedInfrastructure(infra)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center space-x-2 ${
                      selectedInfrastructure?.filename === infra.filename
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{infra.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {selectedInfrastructure && (
          <>
            {/* Informations de l'infrastructure */}
            <div className="px-6 py-4 bg-white border-b">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedInfrastructure.data?.metadata?.title || selectedInfrastructure.name}
                </h2>
                <p className="text-gray-600">
                  {selectedInfrastructure.data?.metadata?.description || selectedInfrastructure.description}
                </p>
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                  <span>{selectedInfrastructure.componentsCount} composants</span>
                  <span>•</span>
                  <span>{selectedInfrastructure.zonesCount} zones</span>
                  <span>•</span>
                  <span>{selectedInfrastructure.typesCount} types</span>
                  <span>•</span>
                  <span>{selectedInfrastructure.activeCount} actifs</span>
                </div>
              </div>
              
              <InfrastructureStats infrastructureData={selectedInfrastructure.data} />
            </div>

            {/* Grille d'infrastructure */}
            <InfrastructureGrid infrastructureData={selectedInfrastructure.data} />
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentInfrastructure;